import { computed } from 'vue';
import { NOTIFICATION_DISPLAY_DURATION } from '@/static/timing';
import { ActAddedUserTypes, ActDateType, ActDateTypes, IActUserToAdd } from '@/models/act/act-access.interface';
import { ProjectProduct, CrmDataProcessorClient, ProjectProductAccess, ProjectDetails }
	from '@studyportals/crm-data-processor-interface';
import { IDashboardAccessPeriodsValidationIssue } from '@/models/act/act-access.interface';
import { ModalErrorStates } from '@/models/modal/modal.enum';
import cookieHandler from '@/infrastructure/cookie-handler';
import actStore from '@/store/act';
import rollbarOperator from '@/infrastructure/rollbar-operator';
import store from '@/store';

class ActPresenter {
	private client: CrmDataProcessorClient;
	private readonly tokenId: string;

	public dashboardAccessPeriodsValidationIssues = computed((): IDashboardAccessPeriodsValidationIssue[] => {
		return this.actDashboardsToAdd.value
			.map((dashboard) => {
				let message = '';
				let field: ActDateType = ActDateTypes.END;

				const currentDate = new Date();
				if (!dashboard.productStartDate) {
					message = 'Start date needs to be filled in.';
					field = ActDateTypes.START;
				} else if (!dashboard.productEndDate) {
					message = 'End date needs to be filled in.';
				} else if (dashboard.productEndDate <= currentDate) {
					message = 'End date must be in the future.';
				} else if (dashboard.productStartDate >= dashboard.productEndDate) {
					message = 'End date must be later than the start date.';
				}

				if (!message) {
					return;
				}

				return {
					productId: dashboard.productNumber,
					field,
					message
				};
			})
			.filter((issue) => issue !== undefined);
	});

	private selectedActInvoiceNumber = computed((): number => {
		return actStore.actGetters.selectedActInvoiceNumber();
	});

	private actUsersToAdd = computed((): IActUserToAdd[] => {
		return actStore.actGetters.actUsersToAdd();
	});

	private actDashboardsToAdd = computed((): ProjectProduct[] => {
		return actStore.actGetters.actDashboardsToAdd();
	});

	private actAccessEntries = computed((): ProjectDetails[] => {
		return actStore.actGetters.actAccessEntries();
	});

	constructor() {
		this.tokenId = cookieHandler.retrieveTokenIdCookie();
		this.client = new CrmDataProcessorClient(
			webpackDefinitions.CRM_DATA_PROCESSOR_URL,
			async () => this.tokenId
		);
	}

	public async retrieveActEntries(): Promise<void> {
		try {
			const dashboards = await this.client.retrieveProjectDetails();
			actStore.actMutations.setActAccessEntries(dashboards);
		} catch (e) {
			rollbarOperator.triggerError(e as Error);
			store.mutations.setModalErrorState(ModalErrorStates.ACT_PROJECT_DETAILS_RETRIEVAL);
		}
	}

	public async retrieveActAvailableDashboards(): Promise<void> {
		try {
			const dashboards = await this.client.retrieveActiveProducts();
			actStore.actMutations.setActAvailableDashboards(dashboards);
		} catch (e) {
			rollbarOperator.triggerError(e as Error);
			store.mutations.setModalErrorState(ModalErrorStates.ACT_DASHBOARDS_RETRIEVAL);
		}
	}

	public async skipDashboardAccess(): Promise<void> {
		const selectedInvoiceNumber = this.selectedActInvoiceNumber.value;
		const relevantProject = this.actAccessEntries.value
			.find(entry => entry.invoiceNumber === selectedInvoiceNumber);
		if (!relevantProject?.projectId) {
			store.mutations.setModalErrorState(ModalErrorStates.ACT_SKIP_ACCESS_PROJECT_INFO_UNAVAILABLE);
			return;
		}

		try {
			await this.client.skipDashboardAccess({
				projectId: relevantProject.projectId,
				invoiceNumber: selectedInvoiceNumber
			});
			store.mutations.setSuccessMessage('Project successfully skipped.');
			setTimeout(() => {
				store.mutations.setSuccessMessage('');
			}, NOTIFICATION_DISPLAY_DURATION);
		} catch (e) {
			rollbarOperator.triggerError(e as Error);
			store.mutations.setModalErrorState(ModalErrorStates.ACT_SKIP_ACCESS_FAILURE);
		}
	}

	public async saveActEntries(): Promise<void> {
		const selectedInvoiceNumber = this.selectedActInvoiceNumber.value;
		const emailAddresses = this.actUsersToAdd.value
			.filter(user => user.type === ActAddedUserTypes.VALID)
			.map(user => user.value);
		const products = this.actDashboardsToAdd.value;
		if (products.some(product => !product.productStartDate || !product.productEndDate)) {
			store.mutations.setModalErrorState(ModalErrorStates.ACT_GRANT_ACCESS_INVALID_DATES);
			return;
		}
		
		const relevantProject = this.actAccessEntries.value
			.find(entry => entry.invoiceNumber === selectedInvoiceNumber);
		if (!relevantProject) {
			store.mutations.setModalErrorState(ModalErrorStates.ACT_GRANT_ACCESS_SAVE_FAILURE);
			return;
		}

		const entriesToSave = products
			.map(dashboard => ({
				projectId: relevantProject.projectId,
				productId: dashboard.productId,
				productStartDate: dashboard.productStartDate,
				productEndDate: dashboard.productEndDate,
				invoiceNumber: selectedInvoiceNumber
			})) as ProjectProductAccess[];
		try {
			await this.client.grantDashboardAccess({
				emailAddresses,
				products: entriesToSave
			});
			store.mutations.setSuccessMessage('Access successfully granted.');
			setTimeout(() => {
				store.mutations.setSuccessMessage('');
			}, NOTIFICATION_DISPLAY_DURATION);
		} catch (e) {
			rollbarOperator.triggerError(e as Error);
			store.mutations.setModalErrorState(ModalErrorStates.ACT_GRANT_ACCESS_SAVE_FAILURE);
		}
	}
}

export default new ActPresenter();
